import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  ExecutarOcorrenciaPayload,
  ExpandirOcorrenciasPayload,
  GerarOSRecorteResponse,
  Ocorrencia,
  PreviewRecorteResponse,
  RecortePayload,
} from '../types/pmocPlanos'
import type { PaginatedResponse } from '../types'

interface OcorrenciaListParams {
  data_inicial?: string
  data_final?: string
  status?: string
  somente_vencidas?: boolean
  somente_pendentes?: boolean
  equipamento?: string
  ambiente?: string
  site?: string
  page?: number
}

export function useOcorrenciasList(pmocId: string, params?: OcorrenciaListParams) {
  return useQuery({
    queryKey: ['ocorrencias', pmocId, params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<Ocorrencia>>(
        `/pmoc/${pmocId}/ocorrencias/`,
        { params },
      )
      return r.data
    },
    enabled: !!pmocId,
  })
}

export function useExpandirOcorrencias(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ExpandirOcorrenciasPayload) => {
      const r = await api.post<{
        criadas: number
        ja_existentes: number
        atualizadas: number
        total: number
      }>(`/pmoc/${pmocId}/expandir-ocorrencias/`, payload)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ocorrencias', pmocId] }),
  })
}

export function useExecutarOcorrencia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: ExecutarOcorrenciaPayload
    }) => {
      const r = await api.patch<Ocorrencia>(`/ocorrencias/${id}/execucao/`, data)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ocorrencias'] }),
  })
}

export function usePreviewRecorte(pmocId: string) {
  return useMutation({
    mutationFn: async (payload: RecortePayload) => {
      const r = await api.get<PreviewRecorteResponse>(
        `/pmoc/${pmocId}/preview-recorte/`,
        { params: payload },
      )
      return r.data
    },
  })
}

export function useGerarOSPorRecorte(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RecortePayload) => {
      const r = await api.post<GerarOSRecorteResponse>(
        `/pmoc/${pmocId}/gerar-os-recorte/`,
        payload,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ocorrencias', pmocId] })
      qc.invalidateQueries({ queryKey: ['os'] })
    },
  })
}
